import React from "react";
import styled from "styled-components";

import { IAnalyticsLayout } from "@formant/core/model/IAnalyticsLayout";
import { IAnalyticsModule } from "@formant/core/model/IAnalyticsModule";
import { IAnalyticsModuleConfiguration } from "@formant/core/model/IAnalyticsModuleConfiguration";
import { IAnalyticsModuleParameters } from "@formant/core/model/IAnalyticsModuleParameters";
import { ISqlQuery } from "@formant/core/model/ISqlQuery";
import { Typography } from "@formant/ui/component/common/Typography";

import { useViewContext } from "../../context/TeleopContext/ViewContext";
import { analyticsModuleStore, observeStore } from "../../stores";
import { addModuleToBottomOfLayout } from "../CustomModuleController";
import { HeaderButton } from "./HeaderButton";
import { RestrictedComponent } from "./RestrictedComponent";

export interface IModuleAddAnalyticsProps {
    deviceIds: string[];
}

export function ModuleAddAnalytics(props: IModuleAddAnalyticsProps) {
    const { deviceIds } = props;
    const { view, layout, onChangeLayout } = useViewContext();

    if (!view || !layout) {
        return null;
    }

    const createModule = async (module: IAnalyticsModule) => {
        if (!view.id) {
            return;
        }
        const newModule = await analyticsModuleStore.save(module);

        const { id } = newModule;
        const { start, end } = getQuery(layout);

        const gridWidth = 12;
        const moduleWidth = 4;
        const moduleHeight = 4;
        const rect = addModuleToBottomOfLayout(
            layout,
            moduleWidth,
            moduleHeight,
        );
        const moduleId = "analytics-module:" + id;

        if (newModule?.query) {
            newModule.query = { ...newModule.query, start, end };
        }

        const configuration = {
            parameters: {
                AnalyticsModuleName: newModule.name,
                AnalyticsModuleDefinitionId: id,
                query: newModule.query,
                configuration: newModule.configuration,
            },
        };

        const values = Object.values(layout).filter(
            _ =>
                !!(_.configuration?.parameters as IAnalyticsModuleParameters)
                    .AnalyticsModuleDefinitionId,
        );
        function addModule(): { x: number; y: number } {
            for (let y = 0; y < 300; y += moduleHeight) {
                for (
                    let x = 0;
                    x <= gridWidth - moduleWidth;
                    x += moduleWidth
                ) {
                    // Check if this position is available
                    if (!isPositionOccupied(x, y, moduleWidth, moduleHeight)) {
                        // If available, add the module and return its position
                        return { x, y };
                    }
                }
            }
            // If no available position found, place it at the bottom right corner
            return { x: gridWidth - moduleWidth, y: gridWidth - moduleHeight };
        }

        function isPositionOccupied(
            x: number,
            y: number,
            width: number,
            height: number,
        ): boolean {
            // Check if any existing module overlaps with the given position and dimensions
            return values.some(
                m =>
                    x < m.x + m.width &&
                    x + width > m.x &&
                    y < m.y + m.height &&
                    y + height > m.y,
            );
        }

        const result = addModule();
        rect.x = result.x;
        rect.y = result.y;

        layout[moduleId] = {
            ...rect,
            configuration: {
                ...configuration,
                moduleType: "analytics",
            },
        };
        view.layout = layout;
        onChangeLayout(layout);
        observeStore.autoScrollToModule = moduleId;
        observeStore.configuringAnalyticsId = id;
    };

    const addBlankChart = async () => {
        const configuration: IAnalyticsModuleConfiguration = {
            aggregateType: "sum",
            chartType: "line",
        };
        const name = "Chart";
        const module = {
            name,
            configuration,
            layout: {},
            data: [],
            query: {
                deviceIds,
                tableColumns: [],
                groupByDevice: deviceIds.length > 1 && deviceIds.length < 8,
                type: undefined,
            } as ISqlQuery,
            streamIds: [],
        } as IAnalyticsModule;

        await createModule(module);
    };

    return (
        <RestrictedComponent requiredAccess="administrator" resource="views">
            <StyledHeaderButton
                icon="plus"
                onClicked={addBlankChart}
                data-tooltip="Add Module"
                data-testid="add-module-button"
            >
                <Typography type="label">Module</Typography>
            </StyledHeaderButton>
        </RestrictedComponent>
    );
}

const getQuery = (layout: IAnalyticsLayout): ISqlQuery => {
    const query = (
        Object.values(layout)?.[0]?.configuration
            ?.parameters as IAnalyticsModuleParameters
    )?.query;
    return {
        start: observeStore.analyticsTempStartDate || query?.start,
        end: observeStore.analyticsTempEndDate || query?.end,
    };
};

const StyledHeaderButton = styled(HeaderButton)`
    && {
        padding-left: 0.75rem !important;
        padding-right: 1rem !important;
    }
    display: flex;
    align-items: center;
    justify-content: center;
`;
